import React from 'react';
import { Helmet } from 'react-helmet-async';
import { brandTitle } from '../../utils/seoMeta';

const SITE_NAME = 'EVs for Idiots';
const SITE_URL = 'https://www.evsforidiots.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  'Thinking about an EV? Straight answers on cost, charging, range and the incentives left in your state, with sourced facts and zero sales pitch. Free tools.';

/**
 * Per-page SEO component. Renders title, meta description, OG/Twitter tags,
 * canonical URL, and optional JSON-LD structured data.
 *
 * `title` is the base title from the shared builders (src/utils/seoMeta.js,
 * stateIncentives.js, the site registry...). brandTitle() appends
 * " | EVs for Idiots" only when the result fits in 60 characters, exactly like
 * the pre-rendered HTML, so the two never drift.
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  jsonLd = null,
  noindex = false,
}) {
  const fullTitle = brandTitle(title);
  const canonical = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,follow" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
